import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input, type InputProps } from './input';

/**
 * Password field — thin wrapper over `Input` that adds an inline show/hide
 * toggle. Inherits every visual property and behavior from Input (border,
 * radius, background, floating label, error state) so password fields stay
 * pixel-identical to other form fields. The eye toggle is injected via
 * Input's `trailingSlot`, which positions it inside the input's relative
 * box (not around the whole component) — so the button stays aligned with
 * the input itself even when a floating label or validation message is
 * present above/below.
 */
export type PasswordInputProps = Omit<InputProps, 'type' | 'trailingSlot'>;

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (props, ref) => {
    const [show, setShow] = React.useState(false);
    return (
      <Input
        {...props}
        ref={ref}
        type={show ? 'text' : 'password'}
        trailingSlot={
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            tabIndex={-1}
            aria-label={show ? 'Hide password' : 'Show password'}
            className="text-white/60 hover:text-white"
          >
            {show ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
          </button>
        }
      />
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
